import * as React from "react";
import { Editor } from "@tiptap/react";

import { Divider } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { colors } from "theme/colors";

import { ColumnActionButton, ActionMenu, MenuItem, MenuBackdrop } from "../table-wrapper/styles";

export const TableColumnActions: React.FC<{
  editor: Editor;
  tableElement: HTMLTableElement;
}> = ({ editor, tableElement }) => {
  const [visibleColumns, setVisibleColumns] = React.useState<
    Array<{ column: HTMLTableCellElement; position: { left: number; top: number } }>
  >([]);

  const [activeMenu, setActiveMenu] = React.useState<{
    columnIndex: number;
    position: { left: number; top: number };
  } | null>(null);

  const [focusedColumnIndex, setFocusedColumnIndex] = React.useState<number | null>(null);

  const updateColumnPositions = React.useCallback((): void => {
    const rect = tableElement.getBoundingClientRect();
    const headerRow = tableElement.querySelector("tr");

    if (!headerRow) return;

    const headerCells = Array.from(headerRow.querySelectorAll("th, td"));

    const columnPositions = headerCells.map((cell) => {
      const cellRect = cell.getBoundingClientRect();
      return {
        column: cell as HTMLTableCellElement,
        position: {
          left: cellRect.left + cellRect.width / 2,
          top: rect.top - 10,
        },
      };
    });

    setVisibleColumns(columnPositions);
  }, [tableElement, activeMenu]);

  /**
   * Updates the focused column index based on the current cursor position in the table
   * This function determines which column the user is currently focused on by:
   * 1. Getting the cursor position from the editor
   * 2. Finding the DOM element at that position
   * 3. Locating the table cell (td/th) that contains the cursor
   * 4. Calculating the column index within that row
   */
  const updateFocusedColumn = React.useCallback((): void => {
    if (!editor) return;

    // Get the current cursor position from the editor state
    const { from } = editor.state.selection;

    // Initialize column index as -1 (not found)
    let columnIndex = -1;

    // Get the DOM position corresponding to the cursor position
    const domPos = editor.view.domAtPos(from);

    // Check if we have a valid DOM node at the cursor position
    if (domPos.node && domPos.node.nodeType === Node.ELEMENT_NODE) {
      const element = domPos.node as Element;

      // Find the closest table cell (td or th) that contains the cursor
      const cellElement = element.closest("td, th") as HTMLTableCellElement | null;

      // Verify the cell is within our target table
      if (cellElement && tableElement.contains(cellElement)) {
        // Find the parent row element
        const rowElement = cellElement.closest("tr");
        if (rowElement) {
          // Get all cells in the current row (both td and th elements)
          const cellsInRow = Array.from(rowElement.querySelectorAll("td, th"));
          // Calculate the column index by finding the position of our cell in the row
          columnIndex = cellsInRow.indexOf(cellElement);
        }
      }
    }

    // Convert column index to null if not found (columnIndex < 0)
    const newFocusedColumnIndex = columnIndex >= 0 ? columnIndex : null;

    // If the focused column has changed and there's an active menu, close it
    // This prevents menus from staying open when the user moves to a different column
    if (focusedColumnIndex !== newFocusedColumnIndex && activeMenu) {
      setActiveMenu(null);
    }

    // Update the focused column index state
    setFocusedColumnIndex(newFocusedColumnIndex);
  }, [editor, tableElement, focusedColumnIndex, activeMenu]);

  React.useEffect(() => {
    updateColumnPositions();

    const handleScroll = (): void => updateColumnPositions();
    const handleResize = (): void => updateColumnPositions();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [updateColumnPositions]);

  React.useEffect(() => {
    if (!editor) return;

    const handleUpdate = (): void => {
      setTimeout(() => {
        updateColumnPositions();
        updateFocusedColumn();
      }, 50);
    };

    const handleSelectionUpdate = (): void => {
      updateFocusedColumn();
    };

    editor.on("update", handleUpdate);
    editor.on("selectionUpdate", handleSelectionUpdate);

    return (): void => {
      editor.off("update", handleUpdate);
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, [editor, updateColumnPositions, updateFocusedColumn]);

  const handleColumnActionClick = (
    columnIndex: number,
    position: { left: number; top: number },
  ): void => {
    setActiveMenu({ columnIndex, position });
  };

  const closeMenu = (): void => {
    setActiveMenu(null);
  };

  const handleInsertColumnLeft = (): void => {
    if (!editor) return;

    editor.chain().focus().addColumnBefore().run();

    closeMenu();
  };

  const handleInsertColumnRight = (): void => {
    if (!editor) return;

    editor.chain().focus().addColumnAfter().run();

    closeMenu();
  };

  const handleDeleteColumn = (): void => {
    if (!editor) return;
    editor.chain().focus().deleteColumn().run();

    closeMenu();
  };

  return (
    <>
      {focusedColumnIndex !== null && visibleColumns[focusedColumnIndex] && (
        <ColumnActionButton
          type="button"
          title="Column actions"
          key={`column-action-${focusedColumnIndex}`}
          onClick={() =>
            handleColumnActionClick(focusedColumnIndex, visibleColumns[focusedColumnIndex].position)
          }
          style={{
            left: `${visibleColumns[focusedColumnIndex].position.left}px`,
            top: `${visibleColumns[focusedColumnIndex].position.top}px`,
            transform: "translateX(-50%)",
          }}
        >
          <DragIndicatorIcon
            htmlColor={colors.common.white}
            sx={{ fontSize: 16, rotate: "90deg" }}
          />
        </ColumnActionButton>
      )}

      {activeMenu && (
        <>
          <MenuBackdrop
            onClick={closeMenu}
            role="button"
            tabIndex={-1}
            aria-label="Close menu"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                closeMenu();
              }
            }}
          />

          <ActionMenu
            style={{
              left: `${activeMenu.position.left}px`,
              top: `${activeMenu.position.top + 25}px`,
              transform: "translateX(-50%)",
            }}
          >
            <MenuItem type="button" onClick={() => handleInsertColumnLeft()}>
              <WestIcon htmlColor={colors.grey[400]} />
              Insert column left
            </MenuItem>

            <MenuItem type="button" onClick={() => handleInsertColumnRight()}>
              <EastIcon htmlColor={colors.grey[400]} />
              Insert column right
            </MenuItem>

            <Divider />

            <MenuItem
              type="button"
              onClick={() => handleDeleteColumn()}
              style={{ color: colors.error.dark }}
            >
              <DeleteOutlineIcon htmlColor={colors.error.dark} />
              Delete column
            </MenuItem>
          </ActionMenu>
        </>
      )}
    </>
  );
};
